import { Component, inject } from '@angular/core';
import { FormBuilder, type FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

export interface ReasonDialogData {
  /** Translate key for the dialog heading. */
  titleKey: string;
  /** Translate key for the textarea label. */
  labelKey: string;
  /** Translate key for the confirm button. */
  confirmKey: string;
  /** Plain text shown under the heading — who or what this affects. Already resolved; not a key. */
  subtitle?: string;
  /**
   * The content being acted on, quoted — e.g. the testimonial's own words — so the officer can
   * see exactly what they are about to hide or cancel without leaving the dialog. Plain text.
   */
  quote?: string;
  /** A short line under the quote (rating, date). Plain text. */
  quoteMeta?: string;
  /**
   * Translate key spelling out what the action does and who is told — the dialog used to show
   * only a heading, a name and an empty box (September 2026). Say the consequences concretely.
   */
  warningKey?: string;
  /** Interpolation params for {@link warningKey} and {@link hintKey}. */
  params?: Record<string, string>;
  /** Translate key under the textarea — who will read the reason. */
  hintKey?: string;
  /** The server's limit for this reason; 1000 (`Limits.MEDIUM`) unless the action allows more. */
  maxLength?: number;
}

/**
 * Collects the mandatory reason behind a negative action, returning
 * `{ reason }` on confirm or `undefined` if the officer backed out.
 *
 * **Every negative action in the app carries a reason as of September 2026**,
 * whoever takes it — declining, cancelling or rescheduling an R-R meeting
 * (`RRMEETING_019`/`020`/`022`), cancelling a referral or closing it as
 * `BUSINESS_LOST` (`REFERRAL_014`), hiding a testimonial, suspending a member
 * (`MEMBER_016`). The server enforces each one; this dialog exists so an officer
 * meets the rule inline rather than through a round-trip failure.
 *
 * Generic on purpose: the actions it serves live in different feature modules,
 * and a second and third copy of the same textarea would have been the point
 * where they started drifting apart. `TestimonialHideDialogComponent` did this
 * job with fixed labels and was retired into this in September 2026; anything
 * new that needs a reason should come here rather than grow its own.
 */
@Component({
  selector: 'app-reason-dialog',
  standalone: false,
  templateUrl: './reason-dialog.component.html',
  styleUrl: './reason-dialog.component.scss'
})
export class ReasonDialogComponent {
  public dialogRef = inject<MatDialogRef<ReasonDialogComponent>>(MatDialogRef);
  public data = inject<ReasonDialogData>(MAT_DIALOG_DATA);
  private fb = inject(FormBuilder);

  form: FormGroup;

  readonly maxLength = this.data.maxLength ?? 1000;

  constructor() {
    this.form = this.fb.group({
      // Not blank: the server trims a reason before requiring it.
      reason: ['', [Validators.required, Validators.pattern(/\S/), Validators.maxLength(this.maxLength)]]
    });
  }

  close(): void {
    this.dialogRef.close();
  }

  save(): void {
    if (this.form.invalid) return;
    this.dialogRef.close(this.form.getRawValue());
  }
}
